import React, { useState } from 'react';
import { Map, Globe, TrendingUp } from 'lucide-react';
import { ComposableMap, Geographies, Geography, ZoomableGroup } from 'react-simple-maps';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/Card';
import Badge from '../components/ui/Badge';
import Skeleton from '../components/ui/Skeleton';
import { useHeatmapData } from '../hooks/useTrends';

const Heatmap = () => {
  const { data, loading } = useHeatmapData();
  const [selectedRegion, setSelectedRegion] = useState(null);

  const getHeatIntensity = (value, maxValue) => {
    const intensity = (value / maxValue) * 100;
    if (intensity > 75) return 'bg-reddit-orange';
    if (intensity > 50) return 'bg-orange-400';
    if (intensity > 25) return 'bg-orange-300';
    return 'bg-orange-200';
  };

  const getMapColor = (value, maxValue) => {
    const intensity = (value / maxValue) * 100;
    if (intensity > 75) return '#FF4500'; // reddit-orange
    if (intensity > 50) return '#FB923C'; // orange-400
    if (intensity > 25) return '#FDBA74'; // orange-300
    return '#FED7AA'; // orange-200
  };

  const maxValue = data.length > 0 ? Math.max(...data.map(d => d.value)) : 1;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold mb-2">Global Trends Heatmap</h1>
        <p className="text-reddit-600 dark:text-dark-textMuted">
          Visualize trending topics by region with interactive heatmap
        </p>
      </div>

      {/* Legend */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <span className="text-sm font-medium">Activity Level:</span>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1">
                <div className="w-6 h-6 bg-orange-200 rounded"></div>
                <span className="text-sm">Low</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-6 h-6 bg-orange-300 rounded"></div>
                <span className="text-sm">Medium</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-6 h-6 bg-orange-400 rounded"></div>
                <span className="text-sm">High</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-6 h-6 bg-reddit-orange rounded"></div>
                <span className="text-sm">Very High</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Heatmap Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading ? (
          <>
            {[...Array(6)].map((_, i) => (
              <Card key={i}>
                <CardContent className="pt-6">
                  <Skeleton className="h-8 w-3/4 mb-4" />
                  <Skeleton className="h-24 w-full mb-3" />
                  <Skeleton className="h-4 w-full" />
                </CardContent>
              </Card>
            ))}
          </>
        ) : data && data.length > 0 ? (
          <>
            {data.map((region) => (
              <Card
                key={region.region}
                className={`cursor-pointer transition-all ${
                  selectedRegion === region.region ? 'ring-2 ring-reddit-orange' : ''
                }`}
                onClick={() => setSelectedRegion(
                  selectedRegion === region.region ? null : region.region
                )}
              >
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <Globe className="w-5 h-5 text-reddit-orange" />
                      <h3 className="font-semibold">{region.region}</h3>
                    </div>
                    <Badge variant="blue">{region.value.toLocaleString()}</Badge>
                  </div>

                  {/* Visual Bar */}
                  <div className="mb-4">
                    <div className="h-24 rounded-lg overflow-hidden bg-reddit-100 dark:bg-dark-border flex items-end">
                      <div
                        className={`w-full ${getHeatIntensity(region.value, maxValue)} transition-all duration-500`}
                        style={{ height: `${(region.value / maxValue) * 100}%` }}
                      ></div>
                    </div>
                  </div>

                  <p className="text-sm text-reddit-600 dark:text-dark-textMuted">
                    {region.value.toLocaleString()} total mentions
                  </p>

                  {selectedRegion === region.region && (
                    <div className="mt-4 pt-4 border-t border-reddit-200 dark:border-dark-border">
                      <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
                        <TrendingUp className="w-4 h-4" />
                        Top Trends in {region.region}
                      </h4>
                      <div className="space-y-2">
                        {region.trends.slice(0, 3).map((trend, idx) => (
                          <div key={idx} className="text-sm flex items-center justify-between">
                            <span className="truncate flex-1">{trend.topic}</span>
                            <Badge variant="orange" className="text-xs ml-2">
                              {trend.mentions.toLocaleString()}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </>
        ) : (
          <div className="col-span-full">
            <Card>
              <CardContent className="py-12 text-center">
                <Globe className="w-16 h-16 text-reddit-400 dark:text-dark-textMuted mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">No Heatmap Data Available</h3>
                <p className="text-reddit-600 dark:text-dark-textMuted">
                  Unable to load regional trending data at this time.
                </p>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* World Map Placeholder */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Map className="w-6 h-6 text-reddit-orange" />
            Interactive World Map
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="bg-reddit-50 dark:bg-dark-bg rounded-lg overflow-hidden">
            <ComposableMap
              projectionConfig={{
                scale: 147,
              }}
              className="w-full h-96"
            >
              <ZoomableGroup>
                <Geographies geography="https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json">
                  {({ geographies }) =>
                    geographies.map((geo) => {
                      // Find matching region data
                      const regionName = geo.properties.name;
                      const regionData = data.find(d => 
                        d.region.toLowerCase().includes(regionName.toLowerCase()) ||
                        regionName.toLowerCase().includes(d.region.toLowerCase())
                      );
                      
                      const fillColor = regionData 
                        ? getMapColor(regionData.value, maxValue)
                        : '#E5E7EB';

                      return (
                        <Geography
                          key={geo.rsmKey}
                          geography={geo}
                          fill={fillColor}
                          stroke="#FFFFFF"
                          strokeWidth={0.5}
                          style={{
                            default: {
                              outline: 'none',
                            },
                            hover: {
                              fill: '#FF4500',
                              outline: 'none',
                              cursor: 'pointer',
                            },
                            pressed: {
                              fill: '#FF5700',
                              outline: 'none',
                            },
                          }}
                          onClick={() => {
                            if (regionData) {
                              setSelectedRegion(
                                selectedRegion === regionData.region ? null : regionData.region
                              );
                            }
                          }}
                        />
                      );
                    })
                  }
                </Geographies>
              </ZoomableGroup>
            </ComposableMap>
            <p className="text-sm text-reddit-500 dark:text-dark-textMuted text-center mt-4 pb-4">
              Click on countries to explore detailed trends • Zoom and pan to navigate
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Heatmap;